<template>
  <div class="dropdown-parent-container">
    <label for="range-dropdown-menu-toggle-button" id="range-dropdown-menu-toggle-button-label" class="sc3-form-label">RANGE</label>
    <button
      type="button"
      title="Range"
      id="range-dropdown-menu-toggle-button"
      class="dropdown-menu-toggle-arrow-icon-button sc3-button sc3-button-small-with-fixed-height sc3-button-white-and-dark-grey"
      :class="{ 'dropdown-menu-toggle-arrow-icon-button-open sc3-button-active': isOpen }"
      ref="rangeDropdownMenuToggleButtonNode"
      @click="toggleDropdown()"
    >
      {{ chartStore.rangeShortTitle }}
      &nbsp;
      <i class="fas fa-angle-down"></i>
    </button>
    <DropdownMenu
      :nodes-excluded-from-closing-on-outside-click="rangeDropdownMenuToggleButtonNode ? [rangeDropdownMenuToggleButtonNode] : []"
      :dropdown-button-left-value="left"
      v-model:is-open="isOpen"
      v-model:max-body-height="dropdownHeight"
      :dropdown-menu-toggle-button-node="rangeDropdownMenuToggleButtonNode"
    >
      <template v-slot:body>
        <ul>
          <template
            v-for="rangeOption in RANGE_OPTIONS_ARRAY"
            :key="rangeOption.title"
          >
            <li v-if="rangeOption.isSimpleRangeSetting && rangeOption.dividerAbove" class="dropdown-divider"></li>
            <li v-if="rangeOption.isSimpleRangeSetting" :class="{ 'selected-dropdown-option': chartStore.range === rangeOption.title }">
              <button
                type="button"
                class="ellipsis-overflow"
                @click="[closeDropdown(), setRange(rangeOption)]"
              >{{ rangeOption.title }}</button>
            </li>
          </template>
        </ul>
      </template>
    </DropdownMenu>
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import { useChartStore } from '@/stores/chartStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import { useModals } from '@/composables/useModals';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';
  import { RANGE_OPTIONS_ARRAY, RangeOption } from '@/enums/range-options';

  const chartStore = useChartStore();
  const { isLoggedIn, isBasic, isExtra } = useAuthentication();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const {
    launchCreateAccountModal,
    launchUpgradeToExtraModal,
    launchUpgradeToProModal
  } = useModals();
  const rangeDropdownMenuToggleButtonNode = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(rangeDropdownMenuToggleButtonNode, { windowResize: true });

  function toggleDropdown() {
    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  function setRange(rangeOption: RangeOption) {
    if (
      !isLoggedIn.value &&
      !rangeOption.isAllowedForFreeUsers
    ) {
      launchCreateAccountModal();
      return;
    }

    if (
      (!isLoggedIn.value || isBasic.value) &&
      !rangeOption.isAllowedForBasicMembers
    ) {
      launchUpgradeToExtraModal();
      return;
    }

    if (
      (!isLoggedIn.value || isBasic.value || isExtra.value) &&
      !rangeOption.isAllowedForExtraMembers
    ) {
      launchUpgradeToProModal();
      return;
    }

    chartStore.$patch({ range: rangeOption.title });
  }

  watch(isOpen, () => {
    if (!isOpen.value) {
      rangeDropdownMenuToggleButtonNode.value?.blur();
    }
  });
</script>

<style scoped>
  .dropdown-parent-container {
    position: relative;
  }

  #range-dropdown-menu-toggle-button-label {
    margin: 0px 0px 1px 3px;
    font-size: 10px;
    line-height: 1;
  }

  #range-dropdown-menu-toggle-button {
    width: 86px;
    margin: 0px;
  }

  ul {
    max-height: v-bind('`${dropdownHeight}px`');
  }
</style>
